import { useCallback, useEffect, useMemo, useState } from 'react'
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, Drawer, Form, Input, Modal, Segmented, Space, Table, Tag, Typography, message } from 'antd'
import { adminApi, formatApiError, type PageResult } from '../../api'
import {
  KNOWLEDGE_SCOPE_OPTIONS_SHORT,
  type KnowledgeScope,
  scopeQuery,
} from '../../constants/knowledgeScope'
import { tablePagination, useServerPagination } from '../../components/ui'

const { Text } = Typography

type GraphEntityRow = {
  id: number
  entity_name: string
  entity_type: string
  properties: Record<string, string>
  property_count: number
  updated_at?: string | null
}

type GraphRelationRow = {
  id: string
  item_id: number
  item_title: string
  relation_index: number
  source_entity: string
  relation: string
  target: string
}

type Props = {
  scope: KnowledgeScope
  onScopeChange: (scope: KnowledgeScope) => void
}

function propsPreview(props: Record<string, string>, max = 2) {
  const entries = Object.entries(props || {})
  if (!entries.length) return '—'
  const head = entries.slice(0, max).map(([k, v]) => `${k}:${v}`)
  const tail = entries.length > max ? ` 等${entries.length}项` : ''
  return head.join('；') + tail
}

export function GraphDataTab({ scope, onScopeChange }: Props) {
  const scopeParams = useMemo(() => scopeQuery(scope), [scope])
  const [view, setView] = useState<'entities' | 'relations'>('entities')
  const [q, setQ] = useState('')
  const [appliedQ, setAppliedQ] = useState('')

  const entityPag = useServerPagination()
  const relPag = useServerPagination()
  const [entityTotal, setEntityTotal] = useState(0)
  const [entities, setEntities] = useState<GraphEntityRow[]>([])
  const [entityLoading, setEntityLoading] = useState(false)
  const [entityDrawerOpen, setEntityDrawerOpen] = useState(false)
  const [editingEntity, setEditingEntity] = useState<GraphEntityRow | null>(null)
  const [entitySaving, setEntitySaving] = useState(false)
  const [entityForm] = Form.useForm<{
    entity_name: string
    entity_type: string
    propRows: { key: string; value: string }[]
  }>()

  const [relTotal, setRelTotal] = useState(0)
  const [relations, setRelations] = useState<GraphRelationRow[]>([])
  const [relLoading, setRelLoading] = useState(false)
  const [relDrawerOpen, setRelDrawerOpen] = useState(false)
  const [editingRelation, setEditingRelation] = useState<GraphRelationRow | null>(null)
  const [relSaving, setRelSaving] = useState(false)
  const [relForm] = Form.useForm<{
    source_entity: string
    relation: string
    target: string
  }>()

  const loadEntities = useCallback(() => {
      setEntityLoading(true)
      const params: Record<string, string | number> = {
        page: entityPag.page,
        page_size: entityPag.pageSize,
        ...scopeParams,
      }
      const kw = appliedQ.trim()
      if (kw) params.q = kw
      return adminApi
        .get<PageResult<GraphEntityRow>>('/admin/knowledge/graph/entities', { params })
        .then((r) => {
          setEntities(r.data.items)
          setEntityTotal(r.data.total)
        })
        .catch((e) => {
          message.error(formatApiError(e, '加载实体失败'))
          setEntities([])
          setEntityTotal(0)
        })
        .finally(() => setEntityLoading(false))
    },
    [appliedQ, scopeParams, entityPag.page, entityPag.pageSize],
  )

  const loadRelations = useCallback(() => {
      setRelLoading(true)
      const params: Record<string, string | number> = {
        page: relPag.page,
        page_size: relPag.pageSize,
        ...scopeParams,
      }
      const kw = appliedQ.trim()
      if (kw) params.q = kw
      return adminApi
        .get<PageResult<GraphRelationRow>>('/admin/knowledge/graph/relations', { params })
        .then((r) => {
          setRelations(r.data.items)
          setRelTotal(r.data.total)
        })
        .catch((e) => {
          message.error(formatApiError(e, '加载关系失败'))
          setRelations([])
          setRelTotal(0)
        })
        .finally(() => setRelLoading(false))
    },
    [appliedQ, scopeParams, relPag.page, relPag.pageSize],
  )

  useEffect(() => {
    entityPag.resetPage()
    relPag.resetPage()
  }, [view, scope, appliedQ, entityPag.resetPage, relPag.resetPage])

  useEffect(() => {
    if (view === 'entities') void loadEntities()
  }, [view, loadEntities])

  useEffect(() => {
    if (view === 'relations') void loadRelations()
  }, [view, loadRelations])

  const openEntityEdit = (row: GraphEntityRow) => {
    setEditingEntity(row)
    entityForm.setFieldsValue({
      entity_name: row.entity_name,
      entity_type: row.entity_type || '',
      propRows: Object.entries(row.properties || {}).map(([key, value]) => ({ key, value })),
    })
    setEntityDrawerOpen(true)
  }

  const saveEntity = () => {
    if (!editingEntity) return
    void entityForm.validateFields().then((v) => {
      const properties: Record<string, string> = {}
      for (const row of v.propRows || []) {
        const k = (row.key || '').trim()
        const val = (row.value || '').trim()
        if (k && val) properties[k] = val
      }
      setEntitySaving(true)
      adminApi
        .put(`/admin/knowledge/graph/entities/${editingEntity.id}`, {
          entity_name: v.entity_name.trim(),
          entity_type: (v.entity_type || '').trim(),
          properties,
        }, { params: scopeParams })
        .then(() => {
          message.success('实体已保存')
          setEntityDrawerOpen(false)
          setEditingEntity(null)
          void loadEntities()
        })
        .catch((e) => message.error(formatApiError(e, '保存失败')))
        .finally(() => setEntitySaving(false))
    })
  }

  const deleteEntity = (row: GraphEntityRow) => {
    Modal.confirm({
      title: '删除实体？',
      okText: '删除',
      okButtonProps: { danger: true },
      content: (
        <>
          将删除实体「{row.entity_name}」及其属性聚合数据；文档中的关系边仍可能引用该名称，需自行在关系列表维护。
        </>
      ),
      onOk: () =>
        adminApi
          .delete(`/admin/knowledge/graph/entities/${row.id}`, { params: scopeParams })
          .then(() => {
            message.success('已删除')
            void loadEntities()
          })
          .catch((e) => message.error(formatApiError(e, '删除失败'))),
    })
  }

  const openRelationEdit = (row: GraphRelationRow) => {
    setEditingRelation(row)
    relForm.setFieldsValue({
      source_entity: row.source_entity,
      relation: row.relation,
      target: row.target,
    })
    setRelDrawerOpen(true)
  }

  const saveRelation = () => {
    if (!editingRelation) return
    void relForm.validateFields().then((v) => {
      setRelSaving(true)
      adminApi
        .put(
          `/admin/knowledge/graph/relations/${editingRelation.item_id}/${editingRelation.relation_index}`,
          {
            source_entity: v.source_entity.trim(),
            relation: (v.relation || '关联').trim(),
            target: v.target.trim(),
          },
          { params: scopeParams },
        )
        .then(() => {
          message.success('关系已保存')
          setRelDrawerOpen(false)
          setEditingRelation(null)
          void loadRelations()
          void loadEntities()
        })
        .catch((e) => message.error(formatApiError(e, '保存失败')))
        .finally(() => setRelSaving(false))
    })
  }

  const deleteRelation = (row: GraphRelationRow) => {
    Modal.confirm({
      title: '删除关系？',
      okText: '删除',
      okButtonProps: { danger: true },
      content: (
        <>
          将从文档「{row.item_title}」中删除该关系，并同步更新图谱与 ES。
        </>
      ),
      onOk: () =>
        adminApi
          .delete(
            `/admin/knowledge/graph/relations/${row.item_id}/${row.relation_index}`,
            { params: scopeParams },
          )
          .then(() => {
            message.success('已删除')
            void loadRelations()
            void loadEntities()
          })
          .catch((e) => message.error(formatApiError(e, '删除失败'))),
    })
  }

  return (
    <>
      <Space style={{ marginBottom: 16 }} wrap>
        <Segmented
          value={scope}
          options={KNOWLEDGE_SCOPE_OPTIONS_SHORT}
          onChange={(v) => onScopeChange(v as KnowledgeScope)}
        />
        <Segmented
          value={view}
          options={[
            { label: '实体', value: 'entities' },
            { label: '关系', value: 'relations' },
          ]}
          onChange={(v) => {
            setView(v as 'entities' | 'relations')
            setQ('')
            setAppliedQ('')
          }}
        />
        <Input.Search
          allowClear
          placeholder={view === 'entities' ? '搜索实体名称/类型' : '搜索实体、关系、文档标题'}
          style={{ width: 260 }}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onSearch={(v) => {
            setQ(v)
            setAppliedQ(v)
          }}
        />
      </Space>

      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        展示已审核入库的图谱实体属性与关系三元组，支持手工编辑；保存后会同步更新图谱聚合表与 ES 图谱索引。
      </Text>

      {view === 'entities' ? (
        <Table
          rowKey="id"
          size="small"
          loading={entityLoading}
          dataSource={entities}
          pagination={tablePagination(
            entityPag.page,
            entityTotal,
            entityPag.handlePaginationChange,
            entityPag.paginationOptions,
          )}
          locale={{ emptyText: appliedQ.trim() ? '未找到匹配实体' : '暂无实体，请先完成抽取并审核入库' }}
          columns={[
            {
              title: '实体名称',
              dataIndex: 'entity_name',
              ellipsis: true,
              render: (v: string) => <Text strong>{v}</Text>,
            },
            {
              title: '分类',
              dataIndex: 'entity_type',
              width: 100,
              render: (v: string) => (v ? <Tag>{v}</Tag> : '—'),
            },
            {
              title: '属性',
              ellipsis: true,
              render: (_, r) => (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {propsPreview(r.properties)}
                </Text>
              ),
            },
            {
              title: '属性数',
              dataIndex: 'property_count',
              width: 72,
              align: 'center' as const,
            },
            {
              title: '操作',
              width: 120,
              fixed: 'right' as const,
              render: (_, r) => (
                <Space size={0}>
                  <Button type="link" size="small" onClick={() => openEntityEdit(r)}>
                    编辑
                  </Button>
                  <Button type="link" size="small" danger onClick={() => deleteEntity(r)}>
                    删除
                  </Button>
                </Space>
              ),
            },
          ]}
        />
      ) : (
        <Table
          rowKey="id"
          size="small"
          loading={relLoading}
          dataSource={relations}
          pagination={tablePagination(
            relPag.page,
            relTotal,
            relPag.handlePaginationChange,
            relPag.paginationOptions,
          )}
          locale={{ emptyText: appliedQ.trim() ? '未找到匹配关系' : '暂无关系，请先完成抽取并审核入库' }}
          columns={[
            {
              title: '源实体',
              dataIndex: 'source_entity',
              width: '18%',
              ellipsis: true,
            },
            {
              title: '关系',
              dataIndex: 'relation',
              width: '12%',
              render: (v: string) => <Tag color="blue">{v}</Tag>,
            },
            {
              title: '目标实体',
              dataIndex: 'target',
              width: '18%',
              ellipsis: true,
            },
            {
              title: '来源文档',
              dataIndex: 'item_title',
              ellipsis: true,
              render: (v: string, r) => (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  #{r.item_id} {v}
                </Text>
              ),
            },
            {
              title: '操作',
              width: 120,
              fixed: 'right' as const,
              render: (_, r) => (
                <Space size={0}>
                  <Button type="link" size="small" onClick={() => openRelationEdit(r)}>
                    编辑
                  </Button>
                  <Button type="link" size="small" danger onClick={() => deleteRelation(r)}>
                    删除
                  </Button>
                </Space>
              ),
            },
          ]}
        />
      )}

      <Drawer
        title={editingEntity ? `编辑实体：${editingEntity.entity_name}` : '编辑实体'}
        open={entityDrawerOpen}
        width={560}
        destroyOnClose
        onClose={() => {
          setEntityDrawerOpen(false)
          setEditingEntity(null)
        }}
        footer={
          <Space style={{ float: 'right' }}>
            <Button onClick={() => setEntityDrawerOpen(false)}>取消</Button>
            <Button type="primary" loading={entitySaving} onClick={saveEntity}>
              保存
            </Button>
          </Space>
        }
      >
        <Form form={entityForm} layout="vertical">
          <Form.Item name="entity_name" label="实体名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input maxLength={256} />
          </Form.Item>
          <Form.Item name="entity_type" label="实体分类">
            <Input maxLength={64} placeholder="如：人员、制度、系统…" />
          </Form.Item>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
            实体属性（键值对）；修改名称会同步更新相关关系中的实体名。
          </Typography.Text>
          <Form.List name="propRows">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...rest }) => (
                  <Space key={key} align="baseline" style={{ display: 'flex', marginBottom: 8 }}>
                    <Form.Item {...rest} name={[name, 'key']} rules={[{ required: true, message: '键' }]}>
                      <Input placeholder="属性名" style={{ width: 140 }} />
                    </Form.Item>
                    <Form.Item {...rest} name={[name, 'value']} rules={[{ required: true, message: '值' }]}>
                      <Input placeholder="属性值" style={{ width: 220 }} />
                    </Form.Item>
                    <MinusCircleOutlined onClick={() => remove(name)} />
                  </Space>
                ))}
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  添加属性
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Drawer>

      <Drawer
        title={editingRelation ? '编辑关系' : '编辑关系'}
        open={relDrawerOpen}
        width={520}
        destroyOnClose
        onClose={() => {
          setRelDrawerOpen(false)
          setEditingRelation(null)
        }}
        footer={
          <Space style={{ float: 'right' }}>
            <Button onClick={() => setRelDrawerOpen(false)}>取消</Button>
            <Button type="primary" loading={relSaving} onClick={saveRelation}>
              保存
            </Button>
          </Space>
        }
      >
        {editingRelation ? (
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            来源文档 #{editingRelation.item_id} {editingRelation.item_title}
          </Text>
        ) : null}
        <Form form={relForm} layout="vertical">
          <Form.Item name="source_entity" label="源实体" rules={[{ required: true }]}>
            <Input maxLength={256} />
          </Form.Item>
          <Form.Item name="relation" label="关系类型" rules={[{ required: true }]}>
            <Input maxLength={128} />
          </Form.Item>
          <Form.Item name="target" label="目标实体" rules={[{ required: true }]}>
            <Input maxLength={256} />
          </Form.Item>
        </Form>
      </Drawer>
    </>
  )
}
